import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  SG_CREATE_DEBRIEF_PDF_REPORT,
  SG_UPDATE_DEBRIEF_PDF_REPORT,
  SG_FETCH_DEBRIEF_PDF_REPORT,
  SG_DELETE_DEBRIEF_PDF_REPORT,
  RESET_DEBRIEF_PDF_REPORT_UPDATES,
  RESET_DEBRIEF_PDF_REPORT,
  SG_GET_ORGANIZATION_SELECT,
} from "constants/actions";

import ReactSelect from "react-select";
import { DEBRIEF_PDF_REPORT_STATUS, REPORT_TYPE_CHOICES } from "data/AdminData";

// shadcn/ui components
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Switch } from "components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Alert, AlertDescription } from "components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Badge } from "components/ui/badge";
import { Separator } from "components/ui/separator";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "components/ui/select";
import { 
  ArrowLeft, 
  Save, 
  FileText, 
  Settings, 
  Palette, 
  Database, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  AlertCircle 
} from "lucide-react";
import ReactJson from "react-json-view";

const DebriefPdfReportForm = ({ initialData = {}, isEdit = false }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const validId = (() => {
    const str = String(id ?? "");
    if (!str || str === "undefined") return null;
    const n = parseInt(str, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const [orgList, setOrgList] = useState([])
  const { get_organization_select } = useSelector(
    (state) => ({
      get_organization_select: state.organizations.organization_select,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (get_organization_select) {
      const orgs = get_organization_select.map(org => ({
        key: org.id,
        text: `(${org.id}) - ${org.name} `,
        label: `(${org.id}) - ${org.name} `,
        value: org.id,
      }));
      setOrgList(orgs);

    }
  }, [get_organization_select]);

  function ChangeSelectedOrg(e) {
    if (e && e.value) {
      const selected = orgList.find(org => org.value === e.value);
      setSelectedOrg(selected);
      setForm(prev => ({ ...prev, organization: e.value }));
    } else {
      // Handle clearing the selection
      setSelectedOrg(null);
      setForm(prev => ({ ...prev, organization: "" })); // Clear the form value too
    }
  }

  const [selectedOrg, setSelectedOrg] = useState(false)

  const [form, setForm] = useState({
    organization: initialData.organization || "",
    debrief_schedule: initialData.debrief_schedule || "",
    status: initialData.status || 1,
    report_type: initialData.report_type || 1,
    template_id: initialData.template_id || 0,
    global_template: initialData.global_template || false,
    org_template: initialData.org_template || false,
    global_template_name: initialData.global_template_name || "",
    org_template_name: initialData.org_template_name || "",
    report_name: initialData.report_name || "",
    description: (() => {
      if (initialData.description) return initialData.description;
      try {
        const p = typeof initialData.params === "string" ? JSON.parse(initialData.params) : (initialData.params || {});
        const ap = p && typeof p.additional_params === "object" ? p.additional_params : null;
        return (ap && ap.description) || "";
      } catch (e) {
        return "";
      }
    })(),
    survey_data: initialData.survey_data ? JSON.stringify(initialData.survey_data, null, 2) : "{}",
    params: initialData.params ? JSON.stringify(initialData.params, null, 2) : "{}",
    additional_info: initialData.additional_info ? JSON.stringify(initialData.additional_info, null, 2) : "[]",
    download_files: initialData.download_files ? JSON.stringify(initialData.download_files, null, 2) : "[]",
    share_report: initialData.share_report ? JSON.stringify(initialData.share_report, null, 2) : "{}",
  });
  // Fetch details for edit mode
  const debriefPdfReportState = useSelector(state => state.debrief_pdf_report);
  useEffect(() => {
    // Ensure any previous create/update flags are cleared so the form does not auto-redirect
    dispatch({ type: RESET_DEBRIEF_PDF_REPORT_UPDATES });
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && validId) {
      dispatch({ type: SG_FETCH_DEBRIEF_PDF_REPORT, payload: { id: validId } });
    }
  }, [dispatch, isEdit, validId]);

  useEffect(() => {
    dispatch({
      type: SG_GET_ORGANIZATION_SELECT,
    })
  }, [dispatch]);

  useEffect(() => {
    if (form.organization && orgList.length) {
      const match = orgList.find((o) => String(o.value) === String(form.organization));
      setSelectedOrg(match || null);
    } else if (!form.organization) {
      setSelectedOrg(null);
    }
  }, [form.organization, orgList]);

  useEffect(() => {
    if (selectedOrg && String(form.organization) !== String(selectedOrg.value)) {
      setForm((prev) => ({ ...prev, organization: selectedOrg.value }));
    }
  }, [selectedOrg]);
  
  useEffect(() => {
    if (isEdit && validId && debriefPdfReportState && debriefPdfReportState[validId]) {
      const data = debriefPdfReportState[validId];
      const parsedForDesc = (() => {
        try { return typeof data.params === "string" ? JSON.parse(data.params) : (data.params || {}); } catch (e) { return {}; }
      })();
      const descFromParams = parsedForDesc && parsedForDesc.additional_params && parsedForDesc.additional_params.description;
      setForm((prev) => ({
        ...prev,
        organization: data.organization ?? prev.organization ?? "",
        debrief_schedule: data.debrief_schedule ?? prev.debrief_schedule ?? "",
        status: data.status ?? prev.status ?? 1,
        report_type: data.report_type ?? prev.report_type ?? 1,
        template_id: data.template_id ?? prev.template_id ?? 0,
        global_template: data.global_template ?? prev.global_template ?? false,
        org_template: data.org_template ?? prev.org_template ?? false,
        global_template_name: data.global_template_name ?? prev.global_template_name ?? "",
        org_template_name: data.org_template_name ?? prev.org_template_name ?? "",
        report_name: data.report_name ?? prev.report_name ?? "",
        description: (data.description ?? descFromParams ?? prev.description ?? ""),
        survey_data: data.survey_data ? JSON.stringify(data.survey_data, null, 2) : (prev.survey_data ?? "{}"),
        params: data.params ? JSON.stringify(data.params, null, 2) : (prev.params ?? "{}"),
        additional_info: data.additional_info ? JSON.stringify(data.additional_info, null, 2) : (prev.additional_info ?? "[]"),
        download_files: data.download_files ? JSON.stringify(data.download_files, null, 2) : (prev.download_files ?? "[]"),
        share_report: data.share_report ? JSON.stringify(data.share_report, null, 2) : (prev.share_report ?? "{}"),
      }));
    }
  }, [debriefPdfReportState, isEdit, validId]);
  const [jsonErrors, setJsonErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fixed params fields state with defaults and custom key/value support
  const RESERVED_PARAM_KEYS = new Set(["js_url", "css_url", "report_name", "img_url", "logo_url", "brand", "additional_params", "area_to_watch_calculation"]);
  const DEFAULT_BRAND = {
    accent: "#007499",
    accentSoft: "#E6F6FA",
    grayText: "#4F4F4F",
    footerText: "#FFFFFF",
  };

  const deriveParamsStateFromObject = (obj) => {
    const safe = obj && typeof obj === "object" ? obj : {};
    const additional = safe && typeof safe.additional_params === "object" && safe.additional_params ? safe.additional_params : null;
    const brand = {
      ...DEFAULT_BRAND,
      ...(additional && typeof additional.brand === "object" && additional.brand ? additional.brand : (typeof safe.brand === "object" && safe.brand ? safe.brand : {})),
    };
    const sourceForCustom = additional || safe;
    const custom = Object.keys(sourceForCustom)
      .filter((k) => !RESERVED_PARAM_KEYS.has(k))
      .map((k, idx) => ({ id: `${k}-${idx}`, key: k, value: String(sourceForCustom[k]) }));
    return {
      js_url: safe.js_url || "",
      css_url: safe.css_url || "",
      report_name: (additional && additional.report_name) || safe.report_name || "",
      img_url: (additional && additional.img_url) || safe.img_url || "",
      logo_url: (additional && additional.logo_url) || safe.logo_url || "",
      area_to_watch_calculation: (additional && additional.area_to_watch_calculation) || safe.area_to_watch_calculation || "",
      brand,
      custom,
    };
  };

  const initialParamsObject = (() => {
    try {
      if (initialData && initialData.params) {
        return typeof initialData.params === "string" ? JSON.parse(initialData.params) : initialData.params;
      }
      return {};
    } catch (e) {
      return {};
    }
  })();

  const [paramsState, setParamsState] = useState(() => deriveParamsStateFromObject(initialParamsObject));

  // When editing, update paramsState once the record loads
  useEffect(() => {
    if (isEdit && validId && debriefPdfReportState && debriefPdfReportState[validId]) {
      const data = debriefPdfReportState[validId];
      const parsed = (() => {
        try { return typeof data.params === "string" ? JSON.parse(data.params) : (data.params || {}); } catch (e) { return {}; }
      })();
      setParamsState(deriveParamsStateFromObject(parsed));
      // If description is empty locally but present in params.additional_params, populate it
      const descFromParams = parsed && parsed.additional_params && parsed.additional_params.description;
      if (descFromParams) {
        setForm((prev) => (prev.description ? prev : { ...prev, description: prev.description || descFromParams }));
      }
    }
  }, [debriefPdfReportState, isEdit, validId]);

  const buildParamsObject = (state) => {
    const additionalParams = {
      report_name: state.report_name || "",
      img_url: state.img_url || "",
      logo_url: state.logo_url || "",
      brand: {
        ...DEFAULT_BRAND,
        ...(state.brand || {}),
      },
    };
    if (state.area_to_watch_calculation) {
      additionalParams.area_to_watch_calculation = state.area_to_watch_calculation;
    }
    // Merge custom keys into additional_params, skipping reserved collisions
    (state.custom || []).forEach((row) => {
      const k = (row && row.key ? String(row.key) : "").trim();
      if (k && !RESERVED_PARAM_KEYS.has(k)) {
        additionalParams[k] = String(row.value ?? "");
      }
    });
    const out = {
      js_url: state.js_url || "",
      css_url: state.css_url || "",
      additional_params: additionalParams,
    };
    return out;
  };

  // Keep form.params JSON in sync whenever paramsState changes
  useEffect(() => {
    const next = buildParamsObject(paramsState);
    setForm((prev) => ({ ...prev, params: JSON.stringify(next, null, 2) }));
    setJsonErrors((prev) => ({ ...prev, params: "" }));
  }, [paramsState]);

  const handleParamsFieldChange = (field, value) => {
    setParamsState((prev) => ({ ...prev, [field]: value }));
  };

  const handleBrandChange = (field, value) => {
    setParamsState((prev) => ({ ...prev, brand: { ...prev.brand, [field]: value } }));
  };

  const addCustomParamRow = () => {
    setParamsState((prev) => ({ ...prev, custom: [...(prev.custom || []), { id: `${Date.now()}-${(prev.custom || []).length}`, key: "", value: "" }] }));
  };

  const updateCustomParamRow = (id, field, value) => {
    setParamsState((prev) => ({
      ...prev,
      custom: (prev.custom || []).map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  };

  const removeCustomParamRow = (id) => {
    setParamsState((prev) => ({ ...prev, custom: (prev.custom || []).filter((row) => row.id !== id) }));
  };

  // TODO: Replace with real org/schedule options from API
  const organizationOptions = useSelector(
    (state) => state.organizations.organization_select?.map((org) => ({
      key: org.id,
      value: org.id,
      text: org.name,
    })) || []
  );
  const scheduleOptions = [];

  const handleChange = (e, { name, value, checked, type }) => {
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleJsonChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    // Validate JSON
    try {
      JSON.parse(value);
      setJsonErrors(prev => ({ ...prev, [name]: "" }));
    } catch (e) {
      setJsonErrors(prev => ({ ...prev, [name]: "Invalid JSON" }));
    }
  };

  const handleSubmit = () => {
    // Validate all JSON fields before submit
    const jsonFields = ["survey_data", "params", "additional_info", "download_files", "share_report"];
    let valid = true;
    let newErrors = {};
    jsonFields.forEach(field => {
      try {
        JSON.parse(form[field]);
        newErrors[field] = "";
      } catch (e) {
        newErrors[field] = "Invalid JSON";
        valid = false;
      }
    });
    setJsonErrors(newErrors);
    if (!valid) return;
    setLoading(true);
    setError("");
    setSuccess("");
    const actionType = isEdit ? SG_UPDATE_DEBRIEF_PDF_REPORT : SG_CREATE_DEBRIEF_PDF_REPORT;
    if (isEdit && !validId) {
      setLoading(false);
      setError("Invalid report id. Cannot update.");
      return;
    }

    // Inject description into params.additional_params to persist if backend omits top-level description
    let paramsWithDescription;
    try {
      const parsedParams = JSON.parse(form.params) || {};
      const existingAdditional = (parsedParams.additional_params && typeof parsedParams.additional_params === "object")
        ? parsedParams.additional_params
        : {};
      paramsWithDescription = {
        ...parsedParams,
        additional_params: form.description
          ? { ...existingAdditional, description: form.description }
          : existingAdditional,
      };
    } catch (e) {
      // Fallback to original params if parse fails; validation above should prevent this path
      paramsWithDescription = JSON.parse(form.params);
    }

    dispatch({
      type: actionType,
      payload: {
        ...(isEdit ? { id: validId } : {}),
        ...form,
        survey_data: JSON.parse(form.survey_data),
        params: paramsWithDescription,
        additional_info: JSON.parse(form.additional_info),
        download_files: JSON.parse(form.download_files),
        share_report: JSON.parse(form.share_report),
      },
    });
  };

  const handleDelete = () => {
    if (!isEdit || !validId) return;
    const confirmDelete = window.confirm("Are you sure you want to delete this PDF report? This action cannot be undone.");
    if (!confirmDelete) return;
    setDeleting(true);
    setLoading(true);
    setError("");
    setSuccess("");
    dispatch({ type: RESET_DEBRIEF_PDF_REPORT });
    dispatch({
      type: SG_DELETE_DEBRIEF_PDF_REPORT,
      payload: { id: validId },
    });
  };

  // Watch for state changes to handle success/error
  useEffect(() => {
    if (debriefPdfReportState.created || debriefPdfReportState.updated) {
      setLoading(false);
      if (deleting) {
        setSuccess("PDF Report deleted successfully.");
      } else {
        setSuccess("PDF Report " + (isEdit ? "updated" : "created") + " successfully.");
      }
      setTimeout(() => navigate("/app/pdf-reports/"), 1200);
    }
  }, [debriefPdfReportState.created, debriefPdfReportState.updated, isEdit, deleting, navigate]);

  useEffect(() => {
    if (debriefPdfReportState.pending === false && loading) {
      setLoading(false);
    }
  }, [debriefPdfReportState.pending, loading]);

  // Build a safe preview of the submission payload for inspection
  const safeParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  };

  // Build preview params including description inside additional_params
  const previewParams = (() => {
    const p = safeParse(form.params);
    if (p && typeof p === "object") {
      const ap = p.additional_params && typeof p.additional_params === "object" ? p.additional_params : {};
      if (form.description) {
        return { ...p, additional_params: { ...ap, description: form.description } };
      }
      return { ...p, additional_params: ap };
    }
    return p;
  })();

  const previewObject = {
    ...(isEdit && validId ? { id: validId } : {}),
    organization: form.organization,
    debrief_schedule: form.debrief_schedule,
    status: form.status,
    report_type: form.report_type,
    template_id: form.template_id,
    global_template: form.global_template,
    org_template: form.org_template,
    global_template_name: form.global_template_name,
    org_template_name: form.org_template_name,
    report_name: form.report_name,
    description: form.description,
    survey_data: safeParse(form.survey_data),
    params: previewParams,
    additional_info: safeParse(form.additional_info),
    download_files: safeParse(form.download_files),
    share_report: safeParse(form.share_report),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/app/pdf-reports/")}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Reports
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                {isEdit ? "Edit" : "Create"} PDF Report
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Configure debrief PDF report settings and parameters
              </p>
            </div>
          </div>
          {form.status && (
            <Badge variant="outline" className="text-xs">
              Status: {form.status}
            </Badge>
          )}
        </div>

        {/* Status Messages */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Main Form Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Report Configuration
            </CardTitle>
            <CardDescription>
              Set up your PDF report with organization details, templates, and customization options
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList>
                <TabsTrigger value="basic" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Basic Settings
                </TabsTrigger>
                <TabsTrigger value="params" className="gap-2">
                  <Palette className="h-4 w-4" />
                  Params & Branding
                </TabsTrigger>
                <TabsTrigger value="advanced" className="gap-2">
                  <Database className="h-4 w-4" />
                  Advanced Data
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Raw JSON
                </TabsTrigger>
              </TabsList>

              {/* Basic Settings Tab */}
              <TabsContent value="basic" className="space-y-6 mt-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization *</Label>
                    <ReactSelect
                      id="organization"
                      name="organization"
                      options={orgList}
                      onChange={ChangeSelectedOrg}
                      isClearable
                      value={selectedOrg}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      placeholder="Select organization..."
                      styles={{
                        container: (base) => ({ ...base, width: '100%' }),
                        control: (base) => ({ 
                          ...base, 
                          minHeight: 40,
                          borderColor: '#e2e8f0',
                          '&:hover': { borderColor: '#cbd5e1' }
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="debrief_schedule">Debrief Schedule</Label>
                    <Input
                      id="debrief_schedule"
                      name="debrief_schedule"
                      placeholder="Enter schedule ID (optional)"
                      value={form.debrief_schedule}
                      onChange={(e) => handleChange(e, { name: 'debrief_schedule', value: e.target.value })}
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid gap-6 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="report_type">Report Type *</Label>
                    <Select
                      value={String(form.report_type)}
                      onValueChange={(value) => handleChange(null, { name: 'report_type', value: parseInt(value) })}
                    >
                      <SelectTrigger id="report_type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {REPORT_TYPE_CHOICES.map((choice) => (
                          <SelectItem key={choice.value} value={String(choice.value)}>
                            {choice.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="template_id">Template ID</Label>
                    <Input
                      id="template_id"
                      name="template_id"
                      type="number"
                      placeholder="0"
                      value={form.template_id}
                      onChange={(e) => handleChange(e, { name: 'template_id', value: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="report_name">Report Name</Label>
                    <Input
                      id="report_name"
                      name="report_name"
                      placeholder="Enter report name"
                      value={form.report_name}
                      onChange={(e) => handleChange(e, { name: 'report_name', value: e.target.value })}
                    />
                  </div>
                </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder="Brief description for this report configuration"
                      value={form.description}
                      onChange={(e) => handleChange(e, { name: 'description', value: e.target.value })}
                      rows={3}
                    />
                  </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700">Template Options</h3>
                  <div className="grid gap-6 md:grid-cols-2">
                    <Card className="bg-slate-50">
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="global_template" className="text-base">
                              Global Template
                            </Label>
                            <p className="text-sm text-slate-600">
                              Use global template settings
                            </p>
                          </div>
                          <Switch
                            id="global_template"
                            checked={form.global_template}
                            onCheckedChange={(checked) => handleChange(null, { name: 'global_template', checked, type: 'checkbox' })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="global_template_name">Global Template Name</Label>
                          <Input
                            id="global_template_name"
                            name="global_template_name"
                            placeholder="Enter template name"
                            value={form.global_template_name}
                            onChange={(e) => handleChange(e, { name: 'global_template_name', value: e.target.value })}
                            disabled={!form.global_template}
                          />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-slate-50">
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor="org_template" className="text-base">
                              Organization Template
                            </Label>
                            <p className="text-sm text-slate-600">
                              Use organization-specific template
                            </p>
                          </div>
                          <Switch
                            id="org_template"
                            checked={form.org_template}
                            onCheckedChange={(checked) => handleChange(null, { name: 'org_template', checked, type: 'checkbox' })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="org_template_name">Org Template Name</Label>
                          <Input
                            id="org_template_name"
                            name="org_template_name"
                            placeholder="Enter template name"
                            value={form.org_template_name}
                            onChange={(e) => handleChange(e, { name: 'org_template_name', value: e.target.value })}
                            disabled={!form.org_template}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Params & Branding Tab */}
              <TabsContent value="params" className="space-y-6 mt-6">
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Core Params</CardTitle>
                    <CardDescription>Configure report naming and visual assets</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor="params_report_name">Report Name (Params)</Label>
                        <Input
                          id="params_report_name"
                          placeholder="Custom report name"
                          value={paramsState.report_name}
                          onChange={(e) => handleParamsFieldChange('report_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="img_url">Image URL</Label>
                        <Input
                          id="img_url"
                          placeholder="https://cdn.example.com/image.png"
                          value={paramsState.img_url}
                          onChange={(e) => handleParamsFieldChange('img_url', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="logo_url">Logo URL</Label>
                        <Input
                          id="logo_url"
                          placeholder="https://cdn.example.com/logo.png"
                          value={paramsState.logo_url}
                          onChange={(e) => handleParamsFieldChange('logo_url', e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Areas to Watch calculation</CardTitle>
                    <CardDescription>
                      Choose how to compute the areas to watch indicator
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="area_to_watch_calculation">Calculation Method</Label>
                        <Select
                          value={paramsState.area_to_watch_calculation || ""}
                          onValueChange={(value) => handleParamsFieldChange('area_to_watch_calculation', value)}
                        >
                          <SelectTrigger id="area_to_watch_calculation">
                            <SelectValue placeholder="Select a method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lowest_scores">Lowest Scores</SelectItem>
                            <SelectItem value="largest_change">Largest negative change</SelectItem>
                            <SelectItem value="largest_org_diff">Biggest difference from org benchmarks</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Palette className="h-5 w-5" />
                      Brand Colors
                    </CardTitle>
                    <CardDescription>
                      Customize the visual theme (defaults applied if empty)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                      {[
                        { key: 'accent', label: 'Accent', default: '#007499' },
                        { key: 'accentSoft', label: 'Accent Soft', default: '#E6F6FA' },
                        { key: 'grayText', label: 'Gray Text', default: '#4F4F4F' },
                        { key: 'footerText', label: 'Footer Text', default: '#FFFFFF' },
                      ].map(({ key, label, default: defaultVal }) => (
                        <div key={key} className="space-y-2">
                          <Label htmlFor={`brand_${key}`} className="flex items-center justify-between">
                            <span>{label}</span>
                            <div 
                              className="h-5 w-5 rounded border border-slate-300 shadow-sm"
                              style={{ backgroundColor: paramsState.brand[key] || defaultVal }}
                            />
                          </Label>
                          <Input
                            id={`brand_${key}`}
                            placeholder={defaultVal}
                            value={paramsState.brand[key]}
                            onChange={(e) => handleBrandChange(key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Custom Parameters</CardTitle>
                        <CardDescription>
                          Add custom key-value pairs for additional configuration
                        </CardDescription>
                      </div>
                      <Button 
                        type="button" 
                        size="sm" 
                        onClick={addCustomParamRow}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Parameter
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(paramsState.custom || []).length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No custom parameters added yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(paramsState.custom || []).map((row) => (
                          <div key={row.id} className="flex gap-3 items-end p-3 bg-slate-50 rounded-lg">
                            <div className="flex-1 space-y-2">
                              <Label htmlFor={`custom_key_${row.id}`}>Key</Label>
                              <Input
                                id={`custom_key_${row.id}`}
                                placeholder="parameter_name"
                                value={row.key}
                                onChange={(e) => updateCustomParamRow(row.id, 'key', e.target.value)}
                              />
                            </div>
                            <div className="flex-1 space-y-2">
                              <Label htmlFor={`custom_value_${row.id}`}>Value</Label>
                              <Input
                                id={`custom_value_${row.id}`}
                                placeholder="parameter value"
                                value={row.value}
                                onChange={(e) => updateCustomParamRow(row.id, 'value', e.target.value)}
                              />
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              onClick={() => removeCustomParamRow(row.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Advanced Data Tab */}
              <TabsContent value="advanced" className="space-y-6 mt-6">
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Asset URLs</CardTitle>
                    <CardDescription>Configure external resources and assets</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="js_url">JavaScript URL</Label>
                        <Input
                          id="js_url"
                          placeholder="https://cdn.example.com/bundle.js"
                          value={paramsState.js_url}
                          onChange={(e) => handleParamsFieldChange('js_url', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="css_url">CSS URL</Label>
                        <Input
                          id="css_url"
                          placeholder="https://cdn.example.com/styles.css"
                          value={paramsState.css_url}
                          onChange={(e) => handleParamsFieldChange('css_url', e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Survey Data</CardTitle>
                      <CardDescription>
                        JSON configuration for survey-related data
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        name="survey_data"
                        value={form.survey_data}
                        onChange={(e) => handleJsonChange("survey_data", e.target.value)}
                        rows={8}
                        className={`font-mono text-sm ${jsonErrors.survey_data ? 'border-red-500' : ''}`}
                        placeholder='{"key": "value"}'
                      />
                      {jsonErrors.survey_data && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {jsonErrors.survey_data}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Additional Info</CardTitle>
                      <CardDescription>
                        Extra metadata and configuration (JSON array)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        name="additional_info"
                        value={form.additional_info}
                        onChange={(e) => handleJsonChange("additional_info", e.target.value)}
                        rows={8}
                        className={`font-mono text-sm ${jsonErrors.additional_info ? 'border-red-500' : ''}`}
                        placeholder='[]'
                      />
                      {jsonErrors.additional_info && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {jsonErrors.additional_info}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Download Files</CardTitle>
                      <CardDescription>
                        Configure downloadable files (JSON array)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        name="download_files"
                        value={form.download_files}
                        onChange={(e) => handleJsonChange("download_files", e.target.value)}
                        rows={8}
                        className={`font-mono text-sm ${jsonErrors.download_files ? 'border-red-500' : ''}`}
                        placeholder='[]'
                      />
                      {jsonErrors.download_files && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {jsonErrors.download_files}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Share Report</CardTitle>
                      <CardDescription>
                        Report sharing configuration (JSON object)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        name="share_report"
                        value={form.share_report}
                        onChange={(e) => handleJsonChange("share_report", e.target.value)}
                        rows={8}
                        className={`font-mono text-sm ${jsonErrors.share_report ? 'border-red-500' : ''}`}
                        placeholder='{"enabled": false}'
                      />
                      {jsonErrors.share_report && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {jsonErrors.share_report}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Raw JSON Preview Tab */}
              <TabsContent value="preview" className="space-y-6 mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Raw JSON Preview</CardTitle>
                    <CardDescription>
                      This is the full payload that will be submitted, with JSON fields parsed when valid.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border p-3">
                      <ReactJson
                        src={previewObject}
                        collapsed={2}
                        enableClipboard={false}
                        displayDataTypes={false}
                        name={null}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/app/pdf-reports/")}
                >
                  Cancel
                </Button>
                {isEdit && validId && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={loading}
                    className="gap-2"
                  >
                    {deleting ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="gap-2 min-w-[140px]"
              >
                {loading && !deleting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEdit ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEdit ? "Update Report" : "Create Report"}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DebriefPdfReportForm;
